import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import MethodologiesBannerImage from "../../../assets/services/asic-design/asicDesign2.png";
import ProfileImage from "../../../assets/blogpages/person.png";
import "./Methodologies.css";
import { AnimatePresence, motion } from "framer-motion";

// Simple highlight component: wraps matches in <mark>
const Highlight = ({ text = "", query = "" }) => {
  if (!query) return <>{text}</>;
  const parts = text.split(new RegExp(`(${query})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase()
      ? <mark key={i}>{part}</mark>
      : part
  );
};

const Methodologies = () => {
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [replyVisible, setReplyVisible] = useState({});

  const closeOverlay = () => setShowPopup(false);
  const handleSearch = () => setSearchTerm(searchInput.trim());
  const handleCommentSubmit = e => {
    e.preventDefault();
    setShowPopup(true);
    e.target.reset();
  };
  const handleReplySubmit = e => {
    e.preventDefault();
    setShowPopup(true);
    e.target.reset();
  };
  const toggleReply = id =>
    setReplyVisible(prev => ({ ...prev, [id]: !prev[id] }));

  // refs for scrolling
  const sectionRefs = {
    intro: useRef(null),
    functional: useRef(null),
    formal: useRef(null),
    regression: useRef(null),
    equivalence: useRef(null),
    induction: useRef(null),
    coverage: useRef(null),
    toolIntegration: useRef(null),
    hybrid: useRef(null),
    bestPractices: useRef(null),
    trends: useRef(null),
    comments: useRef(null),
  };
  const scrollTo = ref =>
    ref.current && ref.current.scrollIntoView({ behavior: "smooth", block: "center" });

  // auto-scroll to first match on search
  useEffect(() => {
    if (!searchTerm) return;
    const firstMark = document.querySelector("mark");
    firstMark?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [searchTerm]);

  // fade-in/out on scroll
  useEffect(() => {
    const io = new IntersectionObserver(
      entries =>
        entries.forEach(e =>
          e.isIntersecting
            ? e.target.classList.add("visible")
            : e.target.classList.remove("visible")
        ),
      { threshold: 0.1 }
    );
    document.querySelectorAll(".fade-section").forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  // auto-dismiss popup
  useEffect(() => {
    if (showPopup) {
      const t = setTimeout(() => setShowPopup(false), 2000);
      return () => clearTimeout(t);
    }
  }, [showPopup]);

  return (
    <div className="blog-post-page methodologies">
      {/* Banner */}
      <div className="blog-banner fade-section">
        <div className="banner-overlay">
          <h1>Verification Methodologies</h1>
          <div className="breadcrumb">
            <Link to="/services/asic-verification">Services</Link> | Methodologies
          </div>
        </div>
      </div>

      {/* Mobile nav */}
      <div className="mobile-nav fade-section">
        <div className="search-box">
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            type="text"
            placeholder="Search keyword..."
            className="search-input"
          />
          <button onClick={handleSearch} className="search-button">
            Search
          </button>
        </div>
        <div className="table-of-contents">
          <h4>In This Article</h4>
          <ul>
            {Object.entries(sectionRefs).map(([key, ref]) => (
              <li key={key}>
                <button onClick={() => scrollTo(ref)}>
                  {{
                    intro: "Introduction",
                    functional: "Functional Verification",
                    formal: "Formal Verification",
                    regression: "Regression Testing",
                    equivalence: "Equivalence Checking",
                    induction: "Induction & Theorem Proving",
                    coverage: "Coverage Closure",
                    toolIntegration: "Tool Flows & Integration",
                    hybrid: "Hybrid Methodologies",
                    bestPractices: "Best Practices",
                    trends: "Future Trends",
                    comments: "Comments",
                  }[key]}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Content */}
      <div className="blog-content-container">
        <div className="blog-left-column">
          <img
            className="post-main-image fade-section"
            src={MethodologiesBannerImage}
            alt="Methodologies"
          />
          <div className="post-info-row fade-section">
            <span className="post-author">By Alex Methodologist</span>
            <span className="post-category">Category: ASIC Verification</span>
            <button
              className="comment-link"
              onClick={() => scrollTo(sectionRefs.comments)}
            >
              3 Comments
            </button>
          </div>

          {/* 1. Introduction */}
          <h2 ref={sectionRefs.intro} className="fade-section">Introduction</h2>
          <p className="fade-section">
            <Highlight
              text="Verification methodologies form the backbone of ASIC development, providing structured approaches to ensure design correctness, performance, and reliability before silicon fabrication."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Whether using directed testing, constrained-random, formal methods, or hybrid flows, selecting the right methodology—or combination thereof—for each project phase is essential to meet time-to-market, cost, and quality targets."
              query={searchTerm}
            />
          </p>

          {/* 2. Functional Verification */}
          <h2 ref={sectionRefs.functional} className="fade-section">Functional Verification</h2>
          <p className="fade-section">
            <Highlight
              text="Functional verification relies on simulating the RTL with stimulus to exercise use cases and corner conditions, catching logical bugs through assertion checks and scoreboards."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Frameworks like UVM standardize the creation of modular testbenches, drivers, monitors, and coverage collectors, enabling reuse across IP blocks and teams."
              query={searchTerm}
            />
          </p>

          {/* 3. Formal Verification */}
          <h2 ref={sectionRefs.formal} className="fade-section">Formal Verification</h2>
          <p className="fade-section">
            <Highlight
              text="Formal methods mathematically prove properties of the design exhaustively, identifying corner-case bugs unreachable by simulation alone."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Model checking, theorem proving, and induction are applied to critical control logic, safety features, and protocol compliance, complementing simulation with absolute guarantees."
              query={searchTerm}
            />
          </p>

          {/* 4. Regression Testing */}
          <h2 ref={sectionRefs.regression} className="fade-section">Regression Testing</h2>
          <p className="fade-section">
            <Highlight
              text="Regression flows re-run existing test suites automatically upon code changes, detecting unintended side-effects and ensuring new features don’t break existing behavior."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Incorporating coverage-driven metrics and test prioritization reduces cycle time while maintaining comprehensive design assessment."
              query={searchTerm}
            />
          </p>

          {/* 5. Equivalence Checking */}
          <h2 ref={sectionRefs.equivalence} className="fade-section">Equivalence Checking</h2>
          <p className="fade-section">
            <Highlight
              text="Equivalence checking formally compares RTL against synthesized netlists, guaranteeing that optimizations and tool transformations preserve intended functionality."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Miter circuits and SAT-based approaches quickly spot mismatches, preventing silicon respins due to synthesis bugs."
              query={searchTerm}
            />
          </p>

          {/* 6. Induction & Theorem Proving */}
          <h2 ref={sectionRefs.induction} className="fade-section">Induction & Theorem Proving</h2>
          <p className="fade-section">
            <Highlight
              text="k-Induction extends model checking by proving properties hold for k cycles and remain invariant thereafter, ideal for pipelined datapaths and control loops."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Interactive theorem provers let engineers craft custom proofs for complex invariants, providing deep confidence in safety-critical logic."
              query={searchTerm}
            />
          </p>

          {/* 7. Coverage Closure */}
          <h2 ref={sectionRefs.coverage} className="fade-section">Coverage Closure</h2>
          <p className="fade-section">
            <Highlight
              text="Coverage metrics—functional, assertion, code, and toggle coverage—measure how thoroughly tests exercise design aspects."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Addressing coverage holes through targeted tests and property refinements ensures that corner cases are not overlooked."
              query={searchTerm}
            />
          </p>

          {/* 8. Tool Flows & Integration */}
          <h2 ref={sectionRefs.toolIntegration} className="fade-section">Tool Flows & Integration</h2>
          <p className="fade-section">
            <Highlight
              text="Unified verification platforms combine simulation, formal, lint, and coverage into single pipelines, simplifying execution and report aggregation."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Automation scripts (Python, Tcl) orchestrate build, run, and report stages, enabling one-click regressions for every code merge."
              query={searchTerm}
            />
          </p>

          {/* 9. Hybrid Methodologies */}
          <h2 ref={sectionRefs.hybrid} className="fade-section">Hybrid Methodologies</h2>
          <p className="fade-section">
            <Highlight
              text="Combining formal proofs for control logic with constrained-random simulation for data paths yields efficient depth and breadth of verification."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Hybrid flows reduce simulation time, focusing exhaustive techniques where they add most value."
              query={searchTerm}
            />
          </p>

          {/* 10. Best Practices */}
          <h2 ref={sectionRefs.bestPractices} className="fade-section">Best Practices</h2>
          <p className="fade-section">
            <Highlight
              text="Start verification planning early, aligning methodology choices with architecture and project risk profile."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Maintain clear ownership of test artifacts, version control stimuli, and continuously review coverage to retire stale tests."
              query={searchTerm}
            />
          </p>

          {/* 11. Future Trends */}
          <h2 ref={sectionRefs.trends} className="fade-section">Future Trends</h2>
          <p className="fade-section">
            <Highlight
              text="AI-driven test generation and proof suggestion promise to further automate methodology selection and coverage closure."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Standardization efforts aim to unify methodology interfaces, enabling cross-vendor interoperability and faster adoption."
              query={searchTerm}
            />
          </p>

          {/* Comment Section */}
          <div className="comment-section" ref={sectionRefs.comments}>
            <h3>3 Comments</h3>

            {/* Comment 1 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img src={ProfileImage} alt="User" className="comment-profile-img" />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">Jane Tester</span>
                    <span className="comment-date">May 12, 2025</span>
                  </div>
                </div>
              </div>
              <p className="comment-text">
                Excellent walkthrough of all methodologies—very comprehensive!
              </p>
            </div>

            {/* Comment 2 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img src={ProfileImage} alt="User" className="comment-profile-img" />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">Proc Guru</span>
                    <span className="comment-date">May 13, 2025</span>
                  </div>
                </div>
              </div>
              <p className="comment-text">
                The hybrid methodologies section really resonated with our latest project!
              </p>
              <button className="reply-button" onClick={() => toggleReply("procguru")}>
                Reply
              </button>
              {replyVisible["procguru"] && (
                <div className="leave-comment">
                  <h3>Reply to Proc Guru</h3>
                  <form onSubmit={handleReplySubmit}>
                    <div className="form-group">
                      <label>Name</label>
                      <input type="text" placeholder="Your name..." required />
                    </div>
                    <div className="form-group">
                      <label>Message</label>
                      <textarea placeholder="Your reply..." required />
                    </div>
                    <button type="submit" className="send-message-btn">
                      Reply
                    </button>
                  </form>
                </div>
              )}
            </div>

            {/* Comment 3 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img src={ProfileImage} alt="User" className="comment-profile-img" />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">Coverage King</span>
                    <span className="comment-date">May 14, 2025</span>
                  </div>
                </div>
              </div>
              <p className="comment-text">
                Great tips on coverage closure, thanks for sharing!
              </p>
            </div>

            {/* Leave a comment */}
            <div className="leave-comment fade-section">
              <h3>Leave a comment</h3>
              <p>Have questions or feedback? Fill out the form below!</p>
              <form onSubmit={handleCommentSubmit}>
                <div className="form-group">
                  <label>Name</label>
                  <input type="text" placeholder="Name here..." required />
                </div>
                <div className="form-group">
                  <label>Message</label>
                  <textarea placeholder="Message here…" required />
                </div>
                <button type="submit" className="send-message-btn">
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="blog-right-column fade-section">
          <div className="search-box">
            <input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              type="text"
              placeholder="Search keyword..."
              className="search-input"
            />
            <button onClick={handleSearch} className="search-button">
              Search
            </button>
          </div>
          <div className="table-of-contents">
            <h4>In This Article</h4>
            <ul>
              {Object.entries(sectionRefs).map(([key, ref]) => (
                <li key={key}>
                  <button onClick={() => scrollTo(ref)}>
                    {{
                      intro: "Introduction",
                      functional: "Functional Verification",
                      formal: "Formal Verification",
                      regression: "Regression Testing",
                      equivalence: "Equivalence Checking",
                      induction: "Induction & Theorem Proving",
                      coverage: "Coverage Closure",
                      toolIntegration: "Tool Flows & Integration",
                      hybrid: "Hybrid Methodologies",
                      bestPractices: "Best Practices",
                      trends: "Future Trends",
                      comments: "Comments",
                    }[key]}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Thank You Popup */}
        <AnimatePresence>
          {showPopup && (
            <motion.div
              className="thank-you-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeOverlay}
            >
              <motion.div
                className="thank-you-content"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                transition={{ type: "spring", stiffness: 300 }}
                onClick={e => e.stopPropagation()}
              >
                <div className="checkmark-container">
                  <motion.svg
                    className="checkmark"
                    viewBox="0 0 52 52"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1, ease: "easeInOut" }}
                  >
                    <circle cx="26" cy="26" r="25" stroke="#007bff" strokeWidth="2" />
                    <path
                      d="M16 27.5L23 34.5L36 19.5"
                      stroke="#007bff"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </motion.svg>
                </div>
                <h3>Thank you for the feedback!</h3>
                <p>We appreciate your comment.</p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Methodologies;